import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import getConfig from "next/config";
import Cookies from "js-cookie";

import { parseMoney } from "../../../Utils/Functions";
import { useUser } from "shared-components/Components/User/User.hook";
import { REGISTER_MUTATION, UPDATE_USER_MUTATION, USER_EXISTS_QUERY } from "shared-components/Components/User/User.querys";
import { getBaseDomain as loginBasePath } from "@Utils/Functions";

export const SEASONS_QUERY = gql`
	query seasons {
		seasons {
			id
			name
		}
	}
`;

const SEND_LEAD_MUTATION = gql`
	mutation sendLead($input: LeadInput!) {
		sendLead(input: $input) {
			id
		}
	}
`;

interface TemporaryRentalHook {
	data: SeasonRow[];
	sendLead: Function;
	loading: Boolean;
}

interface SeasonRow {
	title: string;
	value: Number;
	slug: string;
}

interface ContactInput {
	property_id: number;
	source: number;
	type: string;
	financing_information: boolean;
	message?: string;
	visit_type?: string;
	date?: string;
	isAutoGenerated?: boolean;
	name?: string;
	email?: string;
	phone?: string;
}

export const useTemporaryRental = ({ id, seasons }): TemporaryRentalHook => {
	const { user, isLoggedIn } = useUser();
	// const { loginBasePath } = getConfig().publicRuntimeConfig;
	const [data, setData] = useState([]);

	const { loading: seasonLoading, data: allSeasonsData, error: seasonError } = useQuery(
		SEASONS_QUERY
	);

	const [sendLeadMutation] = useMutation(SEND_LEAD_MUTATION, {
		onError: error => {
			console.error(error);
		},
	});

	const [
		userExistsCustomQuery
	] = useLazyQuery(USER_EXISTS_QUERY, {
		fetchPolicy: "network-only",
	});

	const refreshAuth = data => {
		Cookies.set("frontend_token", data.access_token, {
			expires: 365,
			domain: loginBasePath(),
			path: "/",
		});
		localStorage.setItem("user_md5", data.user_md5);
		user.refetch();
	};

	const [
		registerMutation,
		{ data: dataRegisterMutation }
	] = useMutation(REGISTER_MUTATION);

	const [updateUserMutation] = useMutation(UPDATE_USER_MUTATION);

	const updateUser = ({ name, phone }) => {
		return updateUserMutation({ variables: { name, phone } });
	};

	useEffect(() => {
		if (dataRegisterMutation) {
			refreshAuth(dataRegisterMutation.registerIndividual);
		}
	}, [dataRegisterMutation]);

	const sendLead = async (field: string, extraFields) => {
		let input: ContactInput = {
			type: "PROPERTY",
			property_id: id,
			message: `Me gustaria saber el precio de esta propiedad para la temporada: ${field}`,
			source: 14,
			isAutoGenerated: true,
			financing_information: false,
			name: extraFields?.name,
			email: extraFields?.email,
			phone: extraFields?.phone
		};

		if (!isLoggedIn) {
			return userExistsCustomQuery({ variables: { email: input.email } }).then(data => {
				if (data?.error?.message?.includes("no existe")) {
					return registerMutation({
						variables: {
							email: input.email,
							name: input.name,
						},
					})
						.then(({ data }) => {
							return updateUser({
								name: input.name,
								phone: input.phone,
							}).then(() => {
								user.refetch();
								return sendLeadMutation({
									variables: {
										input,
									},
								}).then(data => {
									user.refetch();
									return data
								});
							});
						})
						.catch(error => {
							console.error(error);
						});
				} else {
					return sendLeadMutation({
						variables: {
							input,
						},
					});
				}
			});
		}

		// TODO: send the variables here
		return sendLeadMutation({
			variables: {
				input,
			},
		});
	};

	useEffect(() => {
		if (seasons && allSeasonsData) {
			let newData = [];
			allSeasonsData.seasons.forEach(season => {
				let seasonIndex = seasons.findIndex(e => e.rental_data?.season_id?.toString() == season.id);

				if (seasonIndex >= 0) {
					newData.push({
						title: season.name,
						slug: season.slug,
						value: `${seasons[seasonIndex].rental_data.currency.name
							} ${parseMoney(seasons[seasonIndex].rental_data.price)}`,
					});
				} else {
					newData.push({
						title: season.name,
						slug: season.slug,
						value: 0,
					});
				}
			});
			setData(newData);
		}
	}, [seasons, allSeasonsData]);

	return {
		data,
		sendLead,
		loading: seasonLoading,
	};
};
